import { useState, useEffect } from 'react';
import api, { adminSystemAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  Settings, Shield, Users, Lock, Key, Bell, AlertTriangle, CheckCircle,
  ChevronRight, ChevronLeft, Save, RefreshCw, Eye, EyeOff, UserCog, Layers, Trash2, Database, Loader2
} from 'lucide-react';

// CRM Sections for display
const SECTION_LABELS = {
  dashboard: 'Dashboard',
  contacts: 'Contacts',
  companies: 'Companies',
  deals: 'Deals',
  sales: 'Sales',
  marketing: 'Marketing',
  commerce: 'Commerce',
  prospecting: 'Prospecting',
  finance_hub: 'Finance Hub',
  service_hub: 'Service Hub',
  hr_hub: 'HR Hub',
  tasks: 'Tasks',
  reports: 'Reports',
  settings: 'Settings'
};

const FEATURE_LABELS = {
  export_data: 'Export Data (CSV/Excel)',
  bulk_delete: 'Bulk Delete',
  bulk_assign: 'Bulk Assign',
  approve_time_off: 'Approve Time Off Requests',
  create_campaigns: 'Create Marketing Campaigns',
  view_reports: 'View Reports',
  access_ai_features: 'Access AI Features',
  manage_integrations: 'Manage Integrations',
  create_users: 'Create Users',
  manage_permissions: 'Manage Permissions',
  view_audit_logs: 'View Audit Logs',
  create_deals: 'Create Deals',
  edit_deals: 'Edit Deals',
  delete_deals: 'Delete Deals',
  create_contacts: 'Create Contacts',
  edit_contacts: 'Edit Contacts',
  delete_contacts: 'Delete Contacts',
  send_emails: 'Send Emails',
  create_quotes: 'Create Quotes',
  approve_quotes: 'Approve Quotes'
};

const PERMISSION_COLORS = {
  full_access: 'bg-emerald-100 text-emerald-700',
  view_only: 'bg-blue-100 text-blue-700',
  no_access: 'bg-red-100 text-red-700'
};

// Main CRM Modules that can be enabled/disabled
const MAIN_MODULES = [
  { key: 'crm', name: 'CRM', description: 'Contacts, Companies, Tickets, Orders', icon: 'Users' },
  { key: 'marketing', name: 'Marketing', description: 'Campaigns, Email Marketing, Social Media', icon: 'Megaphone' },
  { key: 'sales', name: 'Sales', description: 'Deals, Pipeline, Quotes, Forecasting', icon: 'DollarSign' },
  { key: 'finance', name: 'Finance Hub', description: 'Invoices, Budgets, Expenses', icon: 'Wallet' },
  { key: 'service', name: 'Service Hub', description: 'Customer Support, Knowledge Base', icon: 'Headphones' },
  { key: 'hr', name: 'HR Hub', description: 'Employee Management, AI Workforce', icon: 'Users' },
  { key: 'operations', name: 'Operations', description: 'Inventory, Workflows', icon: 'Settings' },
  { key: 'tools', name: 'Tools', description: 'Live Chat, VoIP, Data Hub', icon: 'Wrench' },
  { key: 'reports', name: 'Reports', description: 'Dashboards, Analytics, Builder', icon: 'BarChart' }
];

// Data Cleanup Component for Go-Live Preparation
function DataCleanupTab() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Interactive state - user can move items between preserve and delete
  const [preserveList, setPreserveList] = useState({});
  const [deleteList, setDeleteList] = useState({});
  const [selectedPreserve, setSelectedPreserve] = useState([]);
  const [selectedDelete, setSelectedDelete] = useState([]);

  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    try {
      const res = await adminSystemAPI.previewCleanup();
      setPreview(res.data);
      setPreserveList(res.data.will_preserve || {});
      setDeleteList(res.data.will_delete || {});
      setSelectedPreserve([]);
      setSelectedDelete([]);
    } catch (error) {
      toast.error('Failed to load cleanup preview');
    } finally {
      setLoading(false);
    }
  };

  // Move selected items from Preserve to Delete
  const moveToDelete = () => {
    if (selectedPreserve.length === 0) return;
    
    const newPreserve = { ...preserveList };
    const newDelete = { ...deleteList };
    
    selectedPreserve.forEach(name => {
      if (newPreserve[name]) {
        newDelete[name] = newPreserve[name];
        delete newPreserve[name];
      }
    });
    
    setPreserveList(newPreserve);
    setDeleteList(newDelete);
    setSelectedPreserve([]);
  };

  // Move selected items from Delete to Preserve
  const moveToPreserve = () => {
    if (selectedDelete.length === 0) return;
    
    const newPreserve = { ...preserveList };
    const newDelete = { ...deleteList };
    
    selectedDelete.forEach(name => {
      if (newDelete[name]) {
        newPreserve[name] = newDelete[name];
        delete newDelete[name];
      }
    });
    
    setPreserveList(newPreserve);
    setDeleteList(newDelete);
    setSelectedDelete([]);
  };

  // Toggle selection in preserve list
  const togglePreserveSelection = (name) => {
    setSelectedPreserve(prev => 
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  // Toggle selection in delete list
  const toggleDeleteSelection = (name) => {
    setSelectedDelete(prev => 
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  // Select all in preserve
  const selectAllPreserve = () => {
    const allNames = Object.keys(preserveList);
    setSelectedPreserve(selectedPreserve.length === allNames.length ? [] : allNames);
  };

  // Select all in delete
  const selectAllDelete = () => {
    const allNames = Object.keys(deleteList);
    setSelectedDelete(selectedDelete.length === allNames.length ? [] : allNames);
  };

  const handleCleanup = async () => {
    if (confirmPhrase !== 'CLEAN_FOR_GOLIVE') {
      toast.error('Please type CLEAN_FOR_GOLIVE to confirm');
      return;
    }

    setExecuting(true);
    try {
      const res = await adminSystemAPI.executeCleanup({
        confirm_phrase: confirmPhrase,
        collections_to_preserve: Object.keys(preserveList),
        collections_to_delete: Object.keys(deleteList)
      });
      toast.success(`Cleanup complete! ${res.data.message}`);
      setShowConfirmDialog(false);
      setConfirmPhrase('');
      fetchPreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setExecuting(false);
    }
  };

  // Calculate totals
  const totalPreserve = Object.values(preserveList).reduce((a, b) => a + b, 0);
  const totalDelete = Object.values(deleteList).reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Go-Live Data Cleanup</h3>
              <p className="text-sm text-red-700 mt-1">
                Select which data to keep and which to delete. Use the arrow buttons to move items between columns.
                This action cannot be undone.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Interactive Columns */}
      <div className="flex gap-4 items-start">
        {/* Will Preserve Column */}
        <Card className="border-emerald-200 flex-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
                <CheckCircle className="h-5 w-5" />
                Will Preserve ({totalPreserve} records)
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={selectAllPreserve}
                className="text-xs"
              >
                {selectedPreserve.length === Object.keys(preserveList).length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <CardDescription>Click items to select, then use arrows to move</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              <div className="space-y-1">
                {Object.entries(preserveList).sort((a, b) => a[0].localeCompare(b[0])).map(([name, count]) => (
                  <div 
                    key={name} 
                    onClick={() => togglePreserveSelection(name)}
                    className={`flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                      selectedPreserve.includes(name) 
                        ? 'bg-emerald-200 border-2 border-emerald-400' 
                        : 'bg-emerald-50 hover:bg-emerald-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={selectedPreserve.includes(name)} 
                        className="pointer-events-none"
                      />
                      <span className="font-medium text-emerald-900 text-sm">{name}</span>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">{count}</Badge>
                  </div>
                ))}
                {Object.keys(preserveList).length === 0 && (
                  <p className="text-center py-4 text-muted-foreground">No items to preserve</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Arrow Buttons in the Middle */}
        <div className="flex flex-col gap-2 pt-24">
          <Button 
            variant="outline" 
            size="icon"
            onClick={moveToDelete}
            disabled={selectedPreserve.length === 0}
            className="h-10 w-10 border-red-300 hover:bg-red-50 hover:border-red-400"
            title="Move to Delete"
          >
            <ChevronRight className="h-5 w-5 text-red-600" />
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={moveToPreserve}
            disabled={selectedDelete.length === 0}
            className="h-10 w-10 border-emerald-300 hover:bg-emerald-50 hover:border-emerald-400"
            title="Move to Preserve"
          >
            <ChevronLeft className="h-5 w-5 text-emerald-600" />
          </Button>
        </div>

        {/* Will Delete Column */}
        <Card className="border-red-200 flex-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <Trash2 className="h-5 w-5" />
                Will Delete ({totalDelete} records)
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={selectAllDelete}
                className="text-xs"
              >
                {selectedDelete.length === Object.keys(deleteList).length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <CardDescription>These collections will be cleared</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              <div className="space-y-1">
                {Object.entries(deleteList).sort((a, b) => a[0].localeCompare(b[0])).map(([name, count]) => (
                  <div 
                    key={name} 
                    onClick={() => toggleDeleteSelection(name)}
                    className={`flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                      selectedDelete.includes(name) 
                        ? 'bg-red-200 border-2 border-red-400' 
                        : 'bg-red-50 hover:bg-red-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={selectedDelete.includes(name)} 
                        className="pointer-events-none"
                      />
                      <span className="font-medium text-red-900 text-sm">{name}</span>
                    </div>
                    <Badge className="bg-red-100 text-red-700">{count}</Badge>
                  </div>
                ))}
                {Object.keys(deleteList).length === 0 && (
                  <p className="text-center py-4 text-muted-foreground">No data to delete</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button variant="outline" onClick={fetchPreview}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Reset to Default
        </Button>
        <Button 
          variant="destructive" 
          onClick={() => setShowConfirmDialog(true)}
          disabled={totalDelete === 0}
          data-testid="start-cleanup-btn"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete {totalDelete} Records
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm System Cleanup
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{totalDelete}</strong> records from <strong>{Object.keys(deleteList).length}</strong> collections.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-medium text-slate-600 mb-2">Collections to delete:</p>
              <div className="flex flex-wrap gap-1">
                {Object.keys(deleteList).map(name => (
                  <Badge key={name} variant="outline" className="text-xs">{name}</Badge>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirm">Type <strong>CLEAN_FOR_GOLIVE</strong> to confirm:</Label>
              <Input
                id="confirm"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder="CLEAN_FOR_GOLIVE"
                className="mt-2"
                data-testid="cleanup-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCleanup}
              disabled={executing || confirmPhrase !== 'CLEAN_FOR_GOLIVE'}
              data-testid="confirm-cleanup-btn"
            >
              {executing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cleaning...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState('modules');
  const [loading, setLoading] = useState(true);
  
  // Module configuration
  const [moduleConfig, setModuleConfig] = useState({});
  const [savingModules, setSavingModules] = useState(false);
  
  // Users for permission management
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // Section Permissions
  const [sections, setSections] = useState([]);
  const [permissionLevels, setPermissionLevels] = useState([]);
  const [userSectionPermissions, setUserSectionPermissions] = useState({});
  
  // Feature Permissions
  const [features, setFeatures] = useState([]);
  const [userFeaturePermissions, setUserFeaturePermissions] = useState({});
  
  // Role Templates
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  
  // Escalation Configs
  const [escalationConfigs, setEscalationConfigs] = useState([]);
  const [escalationAlerts, setEscalationAlerts] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchUserPermissions(selectedUserId);
    }
  }, [selectedUserId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch module configuration
      try {
        const moduleRes = await api.get('/module-config');
        if (moduleRes.data) {
          const config = {};
          MAIN_MODULES.forEach(m => {
            config[m.key] = moduleRes.data.enabled_modules?.includes(m.key) ?? true;
          });
          setModuleConfig(config);
        }
      } catch (e) {
        // Initialize all modules as enabled by default
        const defaultConfig = {};
        MAIN_MODULES.forEach(m => { defaultConfig[m.key] = true; });
        setModuleConfig(defaultConfig);
      }
      
      // Fetch sections and features
      const [sectionsRes, featuresRes, usersRes] = await Promise.all([
        api.get('/admin/sections'),
        api.get('/admin/features'),
        api.get('/users')
      ]);
      
      setSections(sectionsRes.data.sections || []);
      setPermissionLevels(sectionsRes.data.permission_levels || []);
      setFeatures(featuresRes.data.features || []);
      setUsers(usersRes.data || []);
      
      // Fetch role templates
      await fetchRoleTemplates();
      
      // Fetch escalation configs
      await fetchEscalationConfigs();
      await fetchEscalationAlerts();
      
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      toast.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = (moduleKey) => {
    setModuleConfig(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleSaveModuleConfig = async () => {
    try {
      setSavingModules(true);
      const enabledModules = Object.entries(moduleConfig)
        .filter(([_, enabled]) => enabled)
        .map(([key]) => key);
      
      await api.put('/module-config', {
        enabled_modules: enabledModules
      });
      
      toast.success('Module configuration saved. Refresh the page to see changes.');
    } catch (error) {
      toast.error('Failed to save module configuration');
    } finally {
      setSavingModules(false);
    }
  };

  const fetchRoleTemplates = async () => {
    try {
      const res = await api.get('/admin/role-templates');
      setRoleTemplates(res.data || []);
    } catch (error) {
      console.error('Failed to fetch role templates:', error);
    }
  };

  const fetchEscalationConfigs = async () => {
    try {
      const res = await api.get('/admin/escalation/configs');
      setEscalationConfigs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch escalation configs:', error);
    }
  };

  const fetchEscalationAlerts = async () => {
    try {
      const res = await api.get('/admin/escalation/alerts');
      setEscalationAlerts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch escalation alerts:', error);
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      const [sectionRes, featureRes] = await Promise.all([
        api.get(`/admin/permissions/sections/${userId}`),
        api.get(`/admin/permissions/features/${userId}`)
      ]);
      
      setUserSectionPermissions(sectionRes.data.permissions || {});
      setUserFeaturePermissions(featureRes.data.effective_permissions || {});
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
    }
  };

  const handleSaveSectionPermissions = async () => {
    if (!selectedUserId) return;
    
    try {
      await api.put('/admin/permissions/sections', {
        user_id: selectedUserId,
        permissions: userSectionPermissions
      });
      toast.success('Section permissions updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleSaveFeaturePermissions = async () => {
    if (!selectedUserId) return;
    
    try {
      await api.put('/admin/permissions/features', {
        user_id: selectedUserId,
        feature_overrides: userFeaturePermissions
      });
      toast.success('Feature permissions updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleSetTemplateDefault = async (templateId) => {
    try {
      await api.put(`/admin/role-templates/${templateId}/set-default`);
      toast.success('Template set as default');
      fetchRoleTemplates();
    } catch (error) {
      toast.error('Failed to set default template');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await api.put(`/admin/escalation/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      fetchEscalationAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await api.put(`/admin/escalation/alerts/${alertId}/resolve`);
      toast.success('Alert resolved');
      fetchEscalationAlerts();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Settings
          </h1>
          <p className="text-muted-foreground">Admin Control Portal - Manage permissions and escalations</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="modules" data-testid="tab-modules">
            <Settings className="h-4 w-4 mr-1" />
            Modules
          </TabsTrigger>
          <TabsTrigger value="section-permissions" data-testid="tab-section-permissions">
            <Layers className="h-4 w-4 mr-1" />
            Sections
          </TabsTrigger>
          <TabsTrigger value="feature-permissions" data-testid="tab-feature-permissions">
            <Key className="h-4 w-4 mr-1" />
            Features
          </TabsTrigger>
          <TabsTrigger value="role-templates" data-testid="tab-role-templates">
            <UserCog className="h-4 w-4 mr-1" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="escalations" data-testid="tab-escalations">
            <Bell className="h-4 w-4 mr-1" />
            Escalations
          </TabsTrigger>
          <TabsTrigger value="data-cleanup" data-testid="tab-data-cleanup" className="text-red-600">
            <AlertTriangle className="h-4 w-4 mr-1" />
            Go-Live Cleanup
          </TabsTrigger>
        </TabsList>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>CRM Module Configuration</CardTitle>
              <CardDescription>
                Enable or disable major CRM modules. Disabled modules will be hidden from the navigation for all users.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {MAIN_MODULES.map(module => (
                  <div 
                    key={module.key}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${moduleConfig[module.key] ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                        <Settings className={`h-5 w-5 ${moduleConfig[module.key] ? 'text-emerald-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium">{module.name}</h4>
                        <p className="text-sm text-muted-foreground">{module.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={moduleConfig[module.key] || false}
                      onCheckedChange={() => handleToggleModule(module.key)}
                      data-testid={`module-toggle-${module.key}`}
                    />
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button onClick={handleSaveModuleConfig} disabled={savingModules}>
                  {savingModules ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Module Configuration
                    </>
                  )}
                </Button>
              </div>
              
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-700">Note</p>
                    <p className="text-sm text-amber-600">
                      Disabling a module will hide it from the navigation menu but won't delete any existing data. 
                      You can re-enable modules at any time. Changes take effect after saving and refreshing the page.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Section Permissions Tab */}
        <TabsContent value="section-permissions" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* User List */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base">Select User</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {users.map(user => (
                  <div
                    key={user.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                      selectedUserId === user.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{user.first_name?.[0]}{user.last_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.role}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Section Permissions Editor */}
            <Card className="lg:col-span-3">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Section Access</CardTitle>
                  <CardDescription>
                    {selectedUserId ? 'Set access level for each CRM section' : 'Select a user to manage permissions'}
                  </CardDescription>
                </div>
                {selectedUserId && (
                  <Button onClick={handleSaveSectionPermissions}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {selectedUserId ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sections.map(section => (
                      <div key={section} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{SECTION_LABELS[section] || section}</p>
                          <Badge className={`${PERMISSION_COLORS[userSectionPermissions[section] || 'no_access']} text-xs mt-1`}>
                            {userSectionPermissions[section] || 'no_access'}
                          </Badge>
                        </div>
                        <Select
                          value={userSectionPermissions[section] || 'no_access'}
                          onValueChange={(val) => setUserSectionPermissions(prev => ({...prev, [section]: val}))}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full_access">
                              <span className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-emerald-600" />
                                Full Access
                              </span>
                            </SelectItem>
                            <SelectItem value="view_only">
                              <span className="flex items-center gap-2">
                                <Eye className="h-4 w-4 text-blue-600" />
                                View Only
                              </span>
                            </SelectItem>
                            <SelectItem value="no_access">
                              <span className="flex items-center gap-2">
                                <EyeOff className="h-4 w-4 text-red-600" />
                                No Access
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Select a user from the list to manage their section permissions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Feature Permissions Tab */}
        <TabsContent value="feature-permissions" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* User List */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base">Select User</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {users.map(user => (
                  <div
                    key={user.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                      selectedUserId === user.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{user.first_name?.[0]}{user.last_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.role}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Feature Permissions Editor */}
            <Card className="lg:col-span-3">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Feature Permissions</CardTitle>
                  <CardDescription>
                    {selectedUserId ? 'Toggle specific features (overrides role defaults)' : 'Select a user to manage permissions'}
                  </CardDescription>
                </div>
                {selectedUserId && (
                  <Button onClick={handleSaveFeaturePermissions}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {selectedUserId ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {features.map(feature => (
                      <div key={feature} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{FEATURE_LABELS[feature] || feature}</p>
                        </div>
                        <Switch
                          checked={userFeaturePermissions[feature] || false}
                          onCheckedChange={(checked) => setUserFeaturePermissions(prev => ({...prev, [feature]: checked}))}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Key className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Select a user from the list to manage their feature permissions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Role Templates Tab */}
        <TabsContent value="role-templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Role Permission Templates</CardTitle>
              <CardDescription>Default permission sets for each role. Users inherit these unless overridden.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roleTemplates.map(template => (
                  <Card key={template.id} className={`${template.is_default ? 'border-blue-300 bg-blue-50' : ''}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{template.name}</h4>
                          <Badge variant="outline" className="mt-1">{template.role}</Badge>
                        </div>
                        {template.is_default && (
                          <Badge className="bg-blue-100 text-blue-700">Default</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{template.description}</p>
                      
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Section Access:</p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(template.section_permissions || {}).slice(0, 5).map(([section, perm]) => (
                            <Badge key={section} className={`${PERMISSION_COLORS[perm]} text-xs`}>
                              {SECTION_LABELS[section]?.substring(0, 8) || section}
                            </Badge>
                          ))}
                          {Object.keys(template.section_permissions || {}).length > 5 && (
                            <Badge variant="outline" className="text-xs">+{Object.keys(template.section_permissions).length - 5} more</Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                        {!template.is_default && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleSetTemplateDefault(template.id)}
                          >
                            Set as Default
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setShowTemplateEditor(true);
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Template Detail Dialog */}
          <Dialog open={showTemplateEditor} onOpenChange={setShowTemplateEditor}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedTemplate?.name} - Permission Details</DialogTitle>
              </DialogHeader>
              {selectedTemplate && (
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium mb-3">Section Permissions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedTemplate.section_permissions || {}).map(([section, perm]) => (
                        <div key={section} className="flex items-center justify-between p-2 border rounded">
                          <span className="text-sm">{SECTION_LABELS[section] || section}</span>
                          <Badge className={`${PERMISSION_COLORS[perm]} text-xs`}>{perm}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-3">Feature Permissions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedTemplate.feature_permissions || {}).map(([feature, enabled]) => (
                        <div key={feature} className="flex items-center justify-between p-2 border rounded">
                          <span className="text-sm">{FEATURE_LABELS[feature] || feature}</span>
                          <Badge className={enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowTemplateEditor(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Escalations Tab */}
        <TabsContent value="escalations" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Escalation Configs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Escalation Rules
                </CardTitle>
                <CardDescription>Configure automatic escalation for overdue items</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {escalationConfigs.map(config => (
                  <div key={config.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{config.name}</h4>
                      <Badge className={config.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                      <div>Entity: <span className="font-medium text-foreground">{config.entity_type}</span></div>
                      <div>Trigger: <span className="font-medium text-foreground">{config.trigger_condition}</span></div>
                      <div>Escalate after: <span className="font-medium text-foreground">{config.escalation_hours}h</span></div>
                      <div>Notify: <span className="font-medium text-foreground">{config.notification_method}</span></div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {config.notify_direct_manager && <Badge variant="outline" className="text-xs">Direct Manager</Badge>}
                      {config.escalate_to_senior_manager && <Badge variant="outline" className="text-xs">Senior Manager</Badge>}
                      {config.escalate_to_department_head && <Badge variant="outline" className="text-xs">Dept Head</Badge>}
                    </div>
                  </div>
                ))}
                {escalationConfigs.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No escalation rules configured</p>
                )}
              </CardContent>
            </Card>

            {/* Active Escalation Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Active Escalations
                </CardTitle>
                <CardDescription>Items that have been escalated to you</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {escalationAlerts.map(alert => (
                  <div key={alert.id} className={`p-4 border rounded-lg ${
                    alert.status === 'active' ? 'border-amber-200 bg-amber-50' : ''
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{alert.entity_type}</Badge>
                          <Badge className={
                            alert.current_escalation_level === 3 ? 'bg-red-100 text-red-700' :
                            alert.current_escalation_level === 2 ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }>
                            Level {alert.current_escalation_level}
                          </Badge>
                        </div>
                        <h4 className="font-medium">{alert.entity_title}</h4>
                        {alert.assigned_user && (
                          <p className="text-sm text-muted-foreground">
                            Assigned to: {alert.assigned_user.first_name} {alert.assigned_user.last_name}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Triggered {formatDistanceToNow(new Date(alert.first_triggered_at), { addSuffix: true })}
                        </p>
                      </div>
                      <Badge className={
                        alert.status === 'active' ? 'bg-amber-100 text-amber-700' :
                        alert.status === 'acknowledged' ? 'bg-blue-100 text-blue-700' :
                        'bg-emerald-100 text-emerald-700'
                      }>
                        {alert.status}
                      </Badge>
                    </div>
                    {alert.status === 'active' && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => handleAcknowledgeAlert(alert.id)}>
                          Acknowledge
                        </Button>
                        <Button size="sm" onClick={() => handleResolveAlert(alert.id)}>
                          Resolve
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                {escalationAlerts.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-300" />
                    <p>No active escalations</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Data Cleanup Tab */}
        <TabsContent value="data-cleanup" className="space-y-6">
          <DataCleanupTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
